import type { SalesLineGroup } from '../enums/sales-packet.enum.js'
import type { CalendarDay } from '../validators/temporal.validator.js'
import { dollarsFromCents } from './format.js'
import { quote, type Quote, type QuoteLine, type QuoteRates } from './sales-money.js'
import { resolveSchedule, scheduleErrors, type ScheduleEntry } from './sales-schedule.js'

/**
 * The rules a packet's lines and schedule follow (docs/sales.spec.md, "Sending
 * a packet"), shared so the builder says what sending will.
 */

export interface PacketLineLike extends QuoteLine {
  group: SalesLineGroup
}

/** What a packet can come to: the cheapest package alone, and the priciest with every add-on. */
export interface PacketRange {
  low: Quote
  high: Quote
}

/** Why these lines can't be sent, or null. */
export function packetLinesError(lines: readonly PacketLineLike[]): string | null {
  if (lines.length === 0) return 'Offer at least one thing.'
  if (!lines.some((l) => l.group === 'package' || l.group === 'included')) return 'Offer a package, or something included.'
  return null
}

/** The lowest and highest totals the couple's choices can reach, with the venue's rates. */
export function packetRange(lines: readonly PacketLineLike[], rates: QuoteRates): PacketRange {
  const included = lines.filter((l) => l.group === 'included')
  const addOns = lines.filter((l) => l.group === 'add_on')
  const packages = lines.filter((l) => l.group === 'package')
  // No package: the included lines are the whole offer.
  const options: PacketLineLike[][] = packages.length === 0 ? [[]] : packages.map((p) => [p])
  const lows = options.map((pick) => quote([...included, ...pick], rates))
  const highs = options.map((pick) => quote([...included, ...pick, ...addOns], rates))
  // `options` is never empty, so neither reduce starts from nothing.
  return {
    low: lows.reduce((a, b) => (b.totalCents < a.totalCents ? b : a)),
    high: highs.reduce((a, b) => (b.totalCents > a.totalCents ? b : a)),
  }
}

/**
 * Why a schedule can't go with these lines, or null: it has to add up to
 * whatever the couple chooses, from the lowest total to the highest.
 */
export function packetScheduleError(
  schedule: readonly ScheduleEntry[],
  range: PacketRange,
  date: CalendarDay,
  today: CalendarDay,
): string | null {
  const [problem] = scheduleErrors(schedule, false)
  if (problem !== undefined) return problem
  for (const total of [range.low.totalCents, range.high.totalCents]) {
    const resolved = resolveSchedule(schedule, total, date, today)
    if ('error' in resolved) {
      return `The payments come to more than ${dollarsFromCents(total)}, the lowest the couple’s choices can total.`
    }
  }
  return null
}

/** Past its last day at the venue: it can't be chosen, signed or paid, and holds no date. */
export function packetLapsed(validUntil: CalendarDay, today: CalendarDay): boolean {
  return validUntil < today
}
